import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  Drawer,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Radio,
  Space,
  Switch,
  Tabs,
} from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxButton } from 'components/RevxButton';
import { RevxSpin } from 'components/RevxSpin';
import {
  EMPTY_MARKUP_RANGE,
  PartialMarkup,
  ReplaceTypeEnum,
} from 'domains/markupDomain';
import { merge } from 'lodash-es';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RULE_TEXT_LEN_255 } from 'utils/validators';
import SkuMarkupRangeList from './SkuMarkupRangeList';
import SkuTransfer from './SkuTransfer';

const SkuMarkupItemForm = () => {
  const { t } = useTranslation();
  const formItemLayout = {
    labelCol: { span: 5 },
    wrapperCol: { span: 18 },
  };
  const formActionsLayout = {
    labelCol: { span: 5 },
    wrapperCol: { span: 18, offset: 5 },
  };
  const isRevshop = useAppSelector(
    (state) => state.markup.contextMode === 'revshop'
  );

  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const saving = useAppSelector(
    (state) => state.loading.effects.markup.saveMarkup
  );
  const item = useAppSelector((state) => state.markup.curSkuMarkup);

  const onClose = () => {
    dispatch.markup.setState({ curSkuMarkup: undefined });
  };
  const onFinish = async (values: any) => {
    let updated: PartialMarkup = merge({}, item, values);
    if (flatOrRange === 'flat') {
      updated.isRange = false;
      updated.markupRanges = updated.markupRanges!.slice(0, 1);
      updated.markupRanges![0].markupPct = values.markupPct;
      updated.markupRanges![0].hasHighCost = false;
      updated.markupRanges![0].lowCost = 0;
      updated.markupRanges![0].skuReplace =
        values.skuReplace || ReplaceTypeEnum.NA;
      if (item?.isRange === true) {
        // used to be range, set default type
        updated.markupRanges![0].skuReplace = isRevshop
          ? ReplaceTypeEnum.NA
          : ReplaceTypeEnum.REPLACE_GLOBAL;
      }
    }
    if (flatOrRange === 'range' && item?.isRange === false) {
      // initialize ranges
      updated.isRange = true;
      updated.markupRanges = [
        {
          ...EMPTY_MARKUP_RANGE,
          hasHighCost: true,
          lowCost: 0,
          highCost: 500,
          markupPct: 10,
          skuReplace: isRevshop
            ? ReplaceTypeEnum.NA
            : ReplaceTypeEnum.REPLACE_GLOBAL,
        },
        {
          ...EMPTY_MARKUP_RANGE,
          hasHighCost: true,
          lowCost: 500,
          highCost: 5000,
          markupPct: 5,
          skuReplace: isRevshop
            ? ReplaceTypeEnum.NA
            : ReplaceTypeEnum.REPLACE_GLOBAL,
        },
        {
          ...EMPTY_MARKUP_RANGE,
          hasHighCost: false,
          lowCost: 5000,
          markupPct: 0,
          skuReplace: isRevshop
            ? ReplaceTypeEnum.NA
            : ReplaceTypeEnum.REPLACE_GLOBAL,
        },
      ];
    }
    await dispatch.markup.saveMarkup(updated);
  };

  const onRemove = async () => {
    if (item?.id) {
      await dispatch.markup.removeMarkup(item);
      onClose();
    }
  };

  const actionBar = (
    <Form.Item {...formActionsLayout}>
      <Space>
        <RevxButton type="primary" htmlType="submit" brandPurple>
          {t('common.save')}
        </RevxButton>
        <RevxButton onClick={onClose}>{t('common.cancel')}</RevxButton>
        {item?.id && (
          <>
            <Popconfirm
              title={t('common.confirmQuestion')}
              icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
              onConfirm={onRemove}
              okText={t('common.ok')}
              cancelText={t('common.cancel')}
            >
              <RevxButton danger>{t('common.remove')}</RevxButton>
            </Popconfirm>
          </>
        )}
      </Space>
    </Form.Item>
  );

  const [flatOrRange, setFlatOrRange] = useState<string>(
    item?.isRange ? 'range' : 'flat'
  );

  return (
    <Drawer
      destroyOnClose
      title={
        item?.id
          ? t('platform.markup.titles.edit')
          : t('platform.markup.titles.create')
      }
      placement="right"
      width={'880px'}
      closable={true}
      onClose={onClose}
      visible={Boolean(item)}
    >
      <RevxSpin spinning={saving}>
        <Tabs
          defaultActiveKey="1"
          style={{ height: '100%', backgroundColor: 'white' }}
        >
          <Tabs.TabPane tab={t('platform.markup.labels.markup')} key="1">
            <Form
              {...formItemLayout}
              layout={'horizontal'}
              form={form}
              initialValues={{
                ...item,
                skuReplace:
                  item?.markupRanges![0].skuReplace ||
                  ReplaceTypeEnum.NO_ADDITIONAL,
                markupPct: item?.markupRanges![0].markupPct || 0,
              }}
              onFinish={onFinish}
            >
              <Form.Item
                label={t('common.active')}
                name="active"
                valuePropName="checked"
              >
                <Switch disabled={!Boolean(item?.id)} />
              </Form.Item>

              <Form.Item
                label={t('common.description')}
                name="description"
                rules={[RULE_TEXT_LEN_255, { required: true }]}
              >
                <Input.TextArea rows={3} />
              </Form.Item>

              <Form.Item label={t('common.type')}>
                <Radio.Group
                  value={flatOrRange}
                  onChange={(e) => setFlatOrRange(e.target.value)}
                >
                  <Radio value="flat">
                    {t('platform.markup.labels.flatMarkup')}
                  </Radio>
                  <Radio value="range">
                    {t('platform.markup.labels.priceRange')}
                  </Radio>
                </Radio.Group>
              </Form.Item>

              {flatOrRange === 'flat' && (
                <>
                  {!isRevshop && (
                    <Form.Item
                      label={t('platform.markup.labels.replace')}
                      name="skuReplace"
                    >
                      <Radio.Group>
                        <Radio value={ReplaceTypeEnum.REPLACE_GLOBAL}>
                          {t('platform.markup.labels.replaceGlobal')}
                        </Radio>
                        <Radio
                          value={ReplaceTypeEnum.REPLACE_GLOBAL_AND_ELEMENT}
                        >
                          {t('platform.markup.labels.replaceGlobalAndElement')}
                        </Radio>
                      </Radio.Group>
                    </Form.Item>
                  )}
                  <Form.Item
                    label={t('platform.markup.labels.markup')}
                    name={'markupPct'}
                  >
                    <InputNumber<number>
                      min={-100}
                      max={100}
                      formatter={(v) => `${v}%`}
                      parser={(v) => Number(v?.replace('%', ''))}
                    ></InputNumber>
                  </Form.Item>
                </>
              )}
              {actionBar}
            </Form>
          </Tabs.TabPane>
          <Tabs.TabPane tab={t('platform.markup.labels.skuCollection')} key="2">
            <SkuTransfer />
          </Tabs.TabPane>
          <Tabs.TabPane
            tab={t('platform.markup.labels.priceRange')}
            key="3"
            disabled={item?.isRange === false}
          >
            <SkuMarkupRangeList />
          </Tabs.TabPane>
        </Tabs>
      </RevxSpin>
    </Drawer>
  );
};
export default SkuMarkupItemForm;
